import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    if (this.dataset.hasUnappliedSplusDiscount === 'true' && !this._splusSyncing) {
      this._splusSyncing = true;
      const body = JSON.stringify({
        sections: [this.sectionId],
        sections_url: window.location.pathname,
      });
      fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
        .then((response) => response.json())
        .then((cartData) => {
          if (cartData?.sections?.[this.sectionId]) {
            morphSection(this.sectionId, cartData.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
          }
        })
        .catch((error) => console.error('SPLUS cart discount sync failed', error));
    }

    if (this.dataset.hasInvalidWelcomeDiscount === 'true' && !this._splusSyncing) {
      this._splusSyncing = true;
      const discountCodes = [];
      const discountPills = document.querySelectorAll('.cart-discount__pill');
      for (const pill of discountPills) {
        if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
          const code = pill.dataset.discountCode;
          if (!code.toUpperCase().includes('WELCOME')) {
            discountCodes.push(code);
          }
        }
      }
      const body = JSON.stringify({
        discount: discountCodes.join(','),
        sections: [this.sectionId],
        sections_url: window.location.pathname,
      });
      fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
        .then((response) => response.json())
        .then((cartData) => {
          if (cartData?.sections?.[this.sectionId]) {
            morphSection(this.sectionId, cartData.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
          }
        })
        .catch((error) => console.error('Invalid welcome discount removal failed', error));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.querySelector(`[data-line="${line}"]`) || this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The click event.
   */
  onLineItemRemove(line, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const cartItemRowToRemove = this.querySelector(`[data-line="${line}"]`) || this.refs.cartItemRows[line - 1];
    if (cartItemRowToRemove) {
      const titleEl = cartItemRowToRemove.querySelector('.cart-items__title, .cart-items__gift-title');
      this.removedProductTitle = titleEl ? titleEl.textContent.trim() : '';
    } else {
      this.removedProductTitle = '';
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      if (!this.isDrawer) {
        const title = this.removedProductTitle;
        this.removedProductTitle = null;
        // Immediately hide the cart columns so only the alert is visible.
        // setProperty with 'important' is required because .cart-page__right-col has display:flex !important in CSS.
        document.querySelectorAll('.cart-page__left-container, .cart-page__right-col').forEach((el) => {
          el.style.setProperty('display', 'none', 'important');
        });
        if (title && typeof window.showCartPageAlert === 'function') {
          // Show the alert (fade-in handled by showCartPageAlert).
          // Pass a no-op so the pending-reload flag is set, blocking an early cart:update reload.
          window.showCartPageAlert(title, function() {});
          // Override the default 5s dismiss timer: after 2s reload immediately with no fade-out.
          const alertEl = document.getElementById('cart-page-alert-container');
          if (alertEl) {
            clearTimeout(alertEl._dismissTimer);
            alertEl._dismissTimer = setTimeout(function() {
              window.location.reload();
            }, 2000);
          }
        } else if (title) {
          // Fallback: save title so DOMContentLoaded can show it after reload
          sessionStorage.setItem('cart-removed-product', title);
        }
        // Do NOT do the view transition; keep cart items hidden during the alert
        return;
      }

      const clone = document.importNode(template.content, true);
      startViewTransition(() => {
        this.replaceChildren(clone);
      }, ['empty-cart-drawer']);
      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const renderUpdatedCart = (cartData) => {
          if (!cartData || !cartData.sections || !cartData.sections[this.sectionId]) return;

          const newSectionHTML = new DOMParser().parseFromString(
            cartData.sections[this.sectionId],
            'text/html'
          );

          // Grab the new cart item count from a hidden element
          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

          // Update data-cart-quantity for all matching variants
          this.#updateQuantitySelectors(cartData);

          this.dispatchEvent(
            new CartUpdateEvent(cartData, this.sectionId, {
              itemCount: newCartItemCount,
              source: 'cart-items-component',
              sections: cartData.sections,
            })
          );

          // On the cart page, when the last item is removed, the alert is already showing
          // (triggered in onLineItemRemove). Skip morphSection so the DOM stays intact
          // while the alert is visible; the page reloads after the alert completes.
          if (!this.isDrawer && newCartItemCount === 0) {
            return;
          }

          morphSection(this.sectionId, cartData.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

          this.#updateCartQuantitySelectorButtonStates();

          if (!this.isDrawer && this.removedProductTitle) {
            const alertContainer = this.querySelector('#cart-page-alert-container') || document.getElementById('cart-page-alert-container');
            if (alertContainer) {
              alertContainer.innerHTML = `
                <div class="cart-page__alert">
                  <span class="cart-page__alert-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M21.3103 7.52156L16.4784 2.68969C16.1963 2.40924 15.815 2.25126 15.4172 2.25H8.58281C8.185 2.25126 7.80371 2.40924 7.52156 2.68969L2.68969 7.52156C2.40924 7.80371 2.25126 8.185 2.25 8.58281V15.4172C2.25126 15.815 2.40924 16.1963 2.68969 16.4784L7.52156 21.3103C7.80371 21.5908 8.185 21.7487 8.58281 21.75H15.4172C15.815 21.7487 16.1963 21.5908 16.4784 21.3103L21.3103 16.4784C21.5908 16.1963 21.7487 15.815 21.75 15.4172V8.58281C21.7487 8.185 21.5908 7.80371 21.3103 7.52156ZM11.25 7.5C11.25 7.30109 11.329 7.11032 11.4697 6.96967C11.6103 6.82902 11.8011 6.75 12 6.75C12.1989 6.75 12.3897 6.82902 12.5303 6.96967C12.671 7.11032 12.75 7.30109 12.75 7.5V12.75C12.75 12.9489 12.671 13.1397 12.5303 13.2803C12.3897 13.421 12.1989 13.5 12 13.5C11.8011 13.5 11.6103 13.421 11.4697 13.2803C11.329 13.1397 11.25 12.9489 11.25 12.75V7.5ZM12 17.25C11.7775 17.25 11.56 17.184 11.375 17.0604C11.19 16.9368 11.0458 16.7611 10.9606 16.5555C10.8755 16.35 10.8532 16.1238 10.8966 15.9055C10.94 15.6873 11.0472 15.4868 11.2045 15.3295C11.3618 15.1722 11.5623 15.065 11.7805 15.0216C11.9988 14.9782 12.225 15.0005 12.4305 15.0856C12.6361 15.1708 12.8118 15.315 12.9354 15.5C13.059 15.685 13.125 15.9025 13.125 16.125C13.125 16.4234 13.0065 16.7095 12.7955 16.9205C12.5845 17.1315 12.2984 17.25 12 17.25Z" fill="#E34229"/>
</svg>
                  </span>
                  <span>A product <strong>${this.removedProductTitle}</strong> has been removed from your cart as it is no longer in stock.</span>
                </div>
              `;
              alertContainer.classList.remove('hidden');
              alertContainer.offsetHeight;
              alertContainer.classList.add('visible');
              clearTimeout(alertContainer._dismissTimer);
              alertContainer._dismissTimer = setTimeout(() => {
                alertContainer.classList.remove('visible');
                const hideOnEnd = () => {
                  alertContainer.classList.add('hidden');
                  alertContainer.removeEventListener('transitionend', hideOnEnd);
                };
                alertContainer.addEventListener('transitionend', hideOnEnd);
              }, 5000);
            }
            this.removedProductTitle = null;
          }
        };

        // Check if only free gift items remain in the cart
        const cartItems = parsedResponseText.items || [];
        const hasRegularItems = cartItems.some(
          (item) => !item.properties || !item.properties._gift_purchase
        );
        const hasGiftItems = cartItems.some(
          (item) => item.properties && item.properties._gift_purchase
        );

        if (hasGiftItems && !hasRegularItems) {
          // If only free gifts remain, we remove them!
          const updates = {};
          cartItems.forEach((item) => {
            if (item.properties && item.properties._gift_purchase) {
              updates[item.key] = 0;
            }
          });

          const updateBody = JSON.stringify({
            updates: updates,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          });

          fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body: updateBody }))
            .then((response) => response.text())
            .then((updateResponseText) => {
              const updatedCart = JSON.parse(updateResponseText);
              renderUpdatedCart(updatedCart);
            })
            .catch((error) => {
              console.error('Failed to auto-remove free gift items from cart', error);
            });
          return;
        }

        renderUpdatedCart(parsedResponseText);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const lineItemRow = this.querySelector(`[data-line="${line}"]`) || this.refs.cartItemRows[line - 1];
    const quantitySelector = lineItemRow?.querySelector('cart-quantity-selector-component, quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

if (!window.__cartRewardRedeemAttached) {
  window.__cartRewardRedeemAttached = true;

  document.addEventListener('click', async function (e) {
    const btn = e.target.closest('.cart-reward-item__btn');
    if (!btn || btn.disabled) return;

    const code = btn.dataset.code;
    const action = btn.dataset.action;
    if (!code) return;

    const cartItemsComponent = document.querySelector('cart-items-component');
    const sectionId = cartItemsComponent ? cartItemsComponent.dataset.sectionId : null;
    if (!sectionId) return;

    const errorContainer = btn.closest('.cart-reward-item').querySelector('.cart-reward-item__error');
    if (errorContainer) {
      errorContainer.style.display = 'none';
      errorContainer.textContent = '';
    }

    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = action === 'remove' ? 'Removing...' : 'Applying...';

    try {
      // Gather existing discounts from the DOM
      let existingDiscounts = [];
      const discountElements = document.querySelectorAll('[data-discount-code]');
      for (const el of discountElements) {
        if (el.dataset.discountCode) {
          existingDiscounts.push(el.dataset.discountCode);
        }
      }

      // Gather all known reward codes from the UI
      const allRewardItems = document.querySelectorAll('.cart-reward-item');
      const knownRewardCodes = Array.from(allRewardItems)
        .map((el) => el.dataset.code)
        .filter(Boolean);

      // Filter out ANY known reward codes from existing discounts so we only have 1 active reward
      existingDiscounts = existingDiscounts.filter((existingCode) => !knownRewardCodes.includes(existingCode));

      let finalDiscounts = '';
      if (action === 'remove') {
        finalDiscounts = existingDiscounts.join(',');
      } else {
        finalDiscounts = [...existingDiscounts, code].join(',');
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: finalDiscounts,
          sections: [sectionId],
        }),
      });

      const updateUrl = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_update_url) 
        ? Theme.routes.cart_update_url 
        : '/cart/update.js';

      const response = await fetch(updateUrl, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.description || 'Discount could not be applied.');
      }

      // Fetch the full cart object to reliably check if the discount applied
      const cartUrl = (typeof window.Shopify !== 'undefined' && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root + 'cart.js'
        : '/cart.js';
      const cartRes = await fetch(cartUrl);
      const cartData = await cartRes.json();

      // Check if the discount was successfully applied in the cart data
      if (action === 'apply') {
        let isAppliedNow = false;
        if (cartData.cart_level_discount_applications) {
          isAppliedNow = cartData.cart_level_discount_applications.some(d => d.title.toUpperCase() === code.toUpperCase());
        }
        if (!isAppliedNow && cartData.items) {
          isAppliedNow = cartData.items.some(item => 
            item.line_level_discount_allocations && 
            item.line_level_discount_allocations.some(a => a.discount_application && a.discount_application.title.toUpperCase() === code.toUpperCase())
          );
        }
        
        if (!isAppliedNow) {
          // Shopify accepted the request but rejected the discount code (e.g. invalid for current items)
          const shopifyErrorMsg = cartData.message || cartData.description || 'This discount cannot be applied to the current items in your cart.';
          throw new Error(shopifyErrorMsg);
        }
      }

      // Dispatch theme's native discount update event
      document.dispatchEvent(new DiscountUpdateEvent(data, 'cart-reward-item'));

      // Use theme's native morphing to update cart drawer seamlessly
      if (data.sections && data.sections[sectionId]) {
        morphSection(sectionId, data.sections[sectionId]);
      }
    } catch (err) {
      console.error('Failed to apply/remove reward discount', err);
      if (errorContainer) {
        errorContainer.textContent = err.message || 'An error occurred while applying the reward.';
        errorContainer.style.display = 'block';
        setTimeout(() => {
          errorContainer.style.display = 'none';
        }, 5000);
      }
      btn.disabled = false;
      btn.textContent = originalText;
    }
  });
}
